import Link from 'next/link';
import classNames from '@/lib/class-names';

// *****************************************
// *
// * PageHeadingWithNavigationRoot
// *
// *****************************************

const PageHeadingWithNavigationRootDefaultElement = 'div';

interface PageHeadingWithNavigationRoot {
  <E extends React.ElementType = typeof PageHeadingWithNavigationRootDefaultElement>(
    props: PageHeadingWithNavigationRootProps<E>,
  ): React.ReactElement<any, any> | null;
  displayName: string;
}

type PageHeadingWithNavigationRootProps<E extends React.ElementType> = PageHeadingWithNavigationRootOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof PageHeadingWithNavigationRootOwnProps>;

type PageHeadingWithNavigationRootOwnProps<E extends React.ElementType = React.ElementType> = {
  as?: E;
  children?: React.ReactNode;
  className?: string;
};

const PageHeadingWithNavigationRoot: PageHeadingWithNavigationRoot = ({
  as: Component = PageHeadingWithNavigationRootDefaultElement,
  children = null,
  className,
  ...rest
}) => {
  return (
    <Component className={className == null ? 'mb-4 flex flex-wrap items-end gap-4' : className} {...rest}>
      {children}
    </Component>
  );
};

PageHeadingWithNavigationRoot.displayName = 'PageHeadingWithNavigation';

// *****************************************
// *
// * PageHeadingWithNavigationHeading
// *
// *****************************************

const PageHeadingWithNavigationHeadingDefaultElement = 'span';

interface PageHeadingWithNavigationHeading {
  <E extends React.ElementType = typeof PageHeadingWithNavigationHeadingDefaultElement>(
    props: PageHeadingWithNavigationHeadingProps<E>,
  ): React.ReactElement | null;
  displayName: string;
}

type PageHeadingWithNavigationHeadingProps<E extends React.ElementType> = PageHeadingWithNavigationHeadingOwnProps<E> &
  Omit<React.ComponentProps<E>, keyof PageHeadingWithNavigationHeadingOwnProps<E>>;

type PageHeadingWithNavigationHeadingOwnProps<E extends React.ElementType = React.ElementType> = {
  as?: E;
  children?: React.ReactNode;
  className?: string;
};

const PageHeadingWithNavigationHeading: PageHeadingWithNavigationHeading = ({
  as: Component = PageHeadingWithNavigationHeadingDefaultElement,
  children,
  className,
  ...rest
}) => {
  return (
    <Component className={className == null ? 'flex-1 text-2xl' : className} {...rest}>
      {children}
    </Component>
  );
};

PageHeadingWithNavigationHeading.displayName = 'PageHeadingWithNavigation_Heading';

// *****************************************
// *
// * PageHeadingWithNavigationNavigation
// *
// *****************************************

const PageHeadingWithNavigationNavigationDefaultElement = 'nav';

interface PageHeadingWithNavigationNavigation {
  <E extends React.ElementType = typeof PageHeadingWithNavigationNavigationDefaultElement>(
    props: PageHeadingWithNavigationNavigationProps<E>,
  ): React.ReactElement<any, any> | null;
  displayName: string;
}

type PageHeadingWithNavigationNavigationProps<E extends React.ElementType> =
  PageHeadingWithNavigationNavigationOwnProps<E> &
    Omit<React.ComponentProps<E>, keyof PageHeadingWithNavigationNavigationOwnProps>;

type PageHeadingWithNavigationNavigationOwnProps<E extends React.ElementType = React.ElementType> = {
  as?: E;
  children?: React.ReactNode;
  className?: string;
};

const PageHeadingWithNavigationNavigation: PageHeadingWithNavigationNavigation = ({
  as: Component = PageHeadingWithNavigationNavigationDefaultElement,
  children,
  className,
  ...rest
}) => {
  return (
    <Component className={className == null ? 'mt-2 flex flex-wrap gap-2' : className} {...rest}>
      {children}
    </Component>
  );
};

PageHeadingWithNavigationNavigation.displayName = 'PageHeadingWithNavigation_Navigation';

// *****************************************
// *
// * PageHeadingWithNavigationNavigationItem
// *
// *****************************************

const PageHeadingWithNavigationNavigationItemDefaultElement = Link;

interface PageHeadingWithNavigationNavigationItem {
  <E extends React.ElementType = typeof PageHeadingWithNavigationNavigationItemDefaultElement>(
    props: PageHeadingWithNavigationNavigationItemProps<E>,
  ): React.ReactElement<any, any> | null;
  displayName: string;
}

type PageHeadingWithNavigationNavigationItemProps<E extends React.ElementType> =
  PageHeadingWithNavigationNavigationItemOwnProps<E> &
    Omit<React.ComponentProps<E>, keyof PageHeadingWithNavigationNavigationItemOwnProps>;

type PageHeadingWithNavigationNavigationItemOwnProps<E extends React.ElementType = React.ElementType> = {
  as?: E;
  children?: React.ReactNode;
  className?: string;
  href: string;
  isActive?: boolean;
  prefetch?: boolean;
  shallow?: boolean;
};

const PageHeadingWithNavigationNavigationItem: PageHeadingWithNavigationNavigationItem = ({
  as: Component = PageHeadingWithNavigationNavigationItemDefaultElement,
  children,
  className,
  href,
  isActive = false,
  prefetch = false,
  shallow = true,
  ...rest
}) => {
  return (
    <Component
      className={className == null ? classNames('text-blue-800', isActive && 'text-blue-400') : className}
      href={href}
      prefetch={prefetch}
      shallow={shallow}
      {...rest}
    >
      {children}
    </Component>
  );
};

PageHeadingWithNavigationNavigationItem.displayName = 'PageHeadingWithNavigation_Navigation_Item';

// *****************************************
// *
// * PageHeadingWithNavigation
// *
// *****************************************

const PageHeadingWithNavigation = Object.assign(PageHeadingWithNavigationRoot, {
  Heading: PageHeadingWithNavigationHeading,
  Navigation: Object.assign(PageHeadingWithNavigationNavigation, {
    Item: PageHeadingWithNavigationNavigationItem,
  }),
});

export default PageHeadingWithNavigation;
